/**
 * EditorToolbar Component
 * Formatting toolbar for the text editor
 */

import { HTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link,
} from 'lucide-react'

export interface EditorToolbarProps extends HTMLAttributes<HTMLDivElement> {
  onFormat?: (format: string) => void
  disabled?: boolean
}

export const EditorToolbar = forwardRef<HTMLDivElement, EditorToolbarProps>(
  ({ onFormat, disabled = false, className, ...props }, ref) => {
    const handleFormat = (format: string) => {
      if (!disabled && onFormat) {
        onFormat(format)
      }
    }

    return (
      <div
        ref={ref}
        className={clsx(
          'bg-white border border-gray-200 rounded-lg p-2',
          'flex items-center gap-1 flex-wrap',
          disabled && 'opacity-50 pointer-events-none',
          className,
        )}
        {...props}
      >
        {/* Text formatting */}
        <ToolbarButton
          icon={<Bold className="h-4 w-4" />}
          title="Bold"
          onClick={() => handleFormat('bold')}
        />
        <ToolbarButton
          icon={<Italic className="h-4 w-4" />}
          title="Italic"
          onClick={() => handleFormat('italic')}
        />
        <ToolbarButton
          icon={<Underline className="h-4 w-4" />}
          title="Underline"
          onClick={() => handleFormat('underline')}
        />
        <ToolbarButton
          icon={<Strikethrough className="h-4 w-4" />}
          title="Strikethrough"
          onClick={() => handleFormat('strikethrough')}
        />

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          icon={<Heading1 className="h-4 w-4" />}
          title="Heading 1"
          onClick={() => handleFormat('h1')}
        />
        <ToolbarButton
          icon={<Heading2 className="h-4 w-4" />}
          title="Heading 2"
          onClick={() => handleFormat('h2')}
        />
        <ToolbarButton
          icon={<Heading3 className="h-4 w-4" />}
          title="Heading 3"
          onClick={() => handleFormat('h3')}
        />

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          icon={<List className="h-4 w-4" />}
          title="Bullet List"
          onClick={() => handleFormat('bulletList')}
        />
        <ToolbarButton
          icon={<ListOrdered className="h-4 w-4" />}
          title="Numbered List"
          onClick={() => handleFormat('numberedList')}
        />

        <ToolbarDivider />

        {/* Block formatting */}
        <ToolbarButton
          icon={<Quote className="h-4 w-4" />}
          title="Quote"
          onClick={() => handleFormat('quote')}
        />
        <ToolbarButton
          icon={<Code className="h-4 w-4" />}
          title="Code Block"
          onClick={() => handleFormat('code')}
        />
        <ToolbarButton
          icon={<Link className="h-4 w-4" />}
          title="Insert Link"
          onClick={() => handleFormat('link')}
        />
      </div>
    )
  },
)

EditorToolbar.displayName = 'EditorToolbar'

interface ToolbarButtonProps {
  icon: React.ReactNode
  title: string
  onClick: () => void
  active?: boolean
}

function ToolbarButton({ icon, title, onClick, active = false }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        'p-2 rounded hover:bg-gray-100 transition-colors',
        active && 'bg-primary-100 text-primary-700',
      )}
      title={title}
      onClick={onClick}
    >
      {icon}
    </button>
  )
}

function ToolbarDivider() {
  return <div className="w-px h-6 bg-gray-300 mx-1" />
}
